import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Ticket, Plus, ArrowLeft } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const STATUS_STYLE = {
  open: "bg-sky-50 text-sky-800 border-sky-200",
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  closed: "bg-muted text-muted-foreground border-border",
};

function StatusBadge({ status }) {
  const st = (status || "open").toLowerCase();
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${STATUS_STYLE[st] || STATUS_STYLE.open}`}>
      {st}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const p = (priority || "normal").toLowerCase();
  const cls = p === "high"
    ? "text-red-700"
    : p === "low"
      ? "text-muted-foreground"
      : "text-foreground";
  return <span className={`text-sm capitalize ${cls}`}>{p}</span>;
}

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function Tickets() {
  const { user } = useAuth();
  const isCustomer = user?.role === "customer";
  const isStaff = user?.role === "admin" || user?.role === "staff";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [appliedQ, setAppliedQ] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("normal");
  const [busy, setBusy] = useState(false);

  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (appliedQ) params.q = appliedQ;
      const { data } = await api.get("/tickets", { params });
      setItems(data.items || []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, appliedQ]);

  useEffect(() => {
    load();
  }, [load]);

  const openTicket = async (id) => {
    setActiveId(id);
    setDetail(null);
    setReply("");
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setDetail(data);
      // Refresh list so unread badges clear
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
      setActiveId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const submitCreate = async (e) => {
    e?.preventDefault?.();
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/tickets", {
        subject: subject.trim(),
        body: body.trim(),
        priority,
      });
      toast.success("Ticket created");
      setCreateOpen(false);
      setSubject("");
      setBody("");
      setPriority("normal");
      await load();
      if (data?.id) openTicket(data.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err));
    } finally {
      setBusy(false);
    }
  };

  const submitReply = async (e) => {
    e?.preventDefault?.();
    if (!reply.trim() || !activeId) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tickets/${activeId}/reply`, { body: reply.trim() });
      setDetail(data);
      setReply("");
      toast.success("Reply sent");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status) => {
    if (!activeId) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/tickets/${activeId}`, { status });
      setDetail(data);
      toast.success(`Marked ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err));
    } finally {
      setBusy(false);
    }
  };

  if (activeId) {
    return (
      <div className="space-y-4" data-testid="ticket-detail">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => { setActiveId(null); setDetail(null); }}
            data-testid="ticket-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          {detail && (
            <div className="flex items-center gap-2 min-w-0">
              <h1 className="text-lg font-semibold truncate">{detail.subject}</h1>
              <StatusBadge status={detail.status} />
            </div>
          )}
        </div>

        {detailLoading && !detail ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : detail ? (
          <>
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              {isStaff && detail.tenant_name && (
                <span>Account: <span className="text-foreground">{detail.tenant_name}</span></span>
              )}
              <span>Priority: <PriorityBadge priority={detail.priority} /></span>
              <span>Opened {fmtWhen(detail.created_at)}</span>
              <div className="ml-auto flex gap-2">
                {detail.status !== "closed" && (
                  <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => setStatus("closed")}>
                    Close
                  </Button>
                )}
                {detail.status === "closed" && (
                  <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => setStatus("open")}>
                    Reopen
                  </Button>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-border bg-card divide-y divide-border max-h-[55vh] overflow-y-auto">
              {(detail.messages || []).map((m) => {
                const mine = m.author_id === user?.id;
                const staffMsg = m.author_role === "admin" || m.author_role === "staff";
                return (
                  <div
                    key={m.id}
                    className={`px-4 py-3 ${mine ? "bg-primary/5" : ""}`}
                    data-testid="ticket-message"
                  >
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <span className="text-sm font-medium">
                        {m.author_name}
                        <span className="ml-2 text-xs font-normal text-muted-foreground capitalize">
                          {staffMsg ? "Support" : m.author_role}
                        </span>
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0">{fmtWhen(m.created_at)}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                  </div>
                );
              })}
            </div>

            {detail.status !== "closed" && (
              <form onSubmit={submitReply} className="space-y-2">
                <Label htmlFor="ticket-reply">Reply</Label>
                <Textarea
                  id="ticket-reply"
                  data-testid="ticket-reply"
                  rows={3}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Write a reply…"
                />
                <Button type="submit" disabled={busy || !reply.trim()} data-testid="ticket-reply-submit">
                  Send reply
                </Button>
              </form>
            )}
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tickets-page">
      <PageHeader
        title="Tickets"
        subtitle={isCustomer
          ? "Contact platform support about billing, routing, or account issues."
          : "Customer support tickets — reply and track status."}
      >
        {isCustomer ? (
          <Button type="button" onClick={() => setCreateOpen(true)} data-testid="ticket-new">
            <Plus className="w-4 h-4 mr-1" weight="bold" /> New ticket
          </Button>
        ) : null}
      </PageHeader>

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setAppliedQ(q.trim());
        }}
      >
        <div className="space-y-1">
          <Label>Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]" data-testid="ticket-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 flex-1 min-w-[160px]">
          <Label htmlFor="ticket-q">Search</Label>
          <Input
            id="ticket-q"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Subject…"
            data-testid="ticket-search"
          />
        </div>
        <Button type="submit" variant="secondary">Search</Button>
      </form>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Ticket}
          title="No tickets"
          description={isCustomer
            ? "Open a ticket to reach support."
            : "No customer tickets match this filter."}
        />
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Subject</TableHead>
                {isStaff && <TableHead>Account</TableHead>}
                <TableHead>Status</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((t) => {
                const unread = isCustomer
                  ? Number(t.unread_for_customer || 0)
                  : Number(t.unread_for_admin || 0);
                return (
                  <TableRow
                    key={t.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => openTicket(t.id)}
                    data-testid={`ticket-row-${t.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {unread > 0 && (
                          <span className="shrink-0 h-2 w-2 rounded-full bg-red-600" title="Unread" />
                        )}
                        <div className="min-w-0">
                          <p className="font-medium truncate">{t.subject}</p>
                          {t.last_message_preview && (
                            <p className="text-xs text-muted-foreground truncate">{t.last_message_preview}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    {isStaff && (
                      <TableCell className="text-sm">{t.tenant_name || t.tenant_id}</TableCell>
                    )}
                    <TableCell><StatusBadge status={t.status} /></TableCell>
                    <TableCell><PriorityBadge priority={t.priority} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {fmtWhen(t.last_message_at || t.updated_at)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New support ticket</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="ticket-subject">Subject</Label>
              <Input
                id="ticket-subject"
                data-testid="ticket-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
                required
              />
            </div>
            <div className="space-y-1">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="ticket-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ticket-body">Message</Label>
              <Textarea
                id="ticket-body"
                data-testid="ticket-body"
                rows={5}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="ticket-create-submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
